import { cn } from '@/lib/utils'
import { endOfWeek, startOfWeek } from '@/utils'
import { useTranslate } from '@ur-solutions/react-hooks'
import {
  differenceInCalendarDays,
  endOfMonth,
  endOfQuarter,
  endOfYear,
  isSameDay,
  startOfMonth,
  startOfQuarter,
  startOfYear,
  subMonths,
  subWeeks,
  subYears,
} from 'date-fns'
import type { DateRange } from 'react-day-picker'

interface QuickRange {
  label: string
  range: {
    from: Date
    to: Date
  }
}

interface Props {
  value: DateRange | null
  onClick: (range: DateRange) => void
  /** Hide presets whose span (inclusive) exceeds this many days. */
  maxRangeDays?: number
}

export function QuickRanges({ value, onClick, maxRangeDays }: Props) {
  const translations = useTranslate({
    lastWeek: 'components.date-picker-quick-ranges.last-week',
    thisWeek: 'components.date-picker-quick-ranges.this-week',
    lastMonth: 'components.date-picker-quick-ranges.last-month',
    thisMonth: 'components.date-picker-quick-ranges.this-month',
    lastQuarter: 'components.date-picker-quick-ranges.last-quarter',
    thisQuarter: 'components.date-picker-quick-ranges.this-quarter',
    lastYear: 'components.date-picker-quick-ranges.last-year',
    thisYear: 'components.date-picker-quick-ranges.this-year',
  })

  const now = new Date()
  const ranges: QuickRange[] = [
    {
      label: translations.lastWeek,
      range: {
        from: subWeeks(startOfWeek(now), 1),
        to: subWeeks(endOfWeek(now), 1),
      },
    },
    {
      label: translations.thisWeek,
      range: {
        from: startOfWeek(now),
        to: endOfWeek(now),
      },
    },
    {
      label: translations.lastMonth,
      range: {
        from: startOfMonth(subMonths(now, 1)),
        to: endOfMonth(subMonths(now, 1)),
      },
    },
    {
      label: translations.thisMonth,
      range: {
        from: startOfMonth(now),
        to: endOfMonth(now),
      },
    },
    {
      label: translations.lastQuarter,
      range: {
        from: startOfQuarter(subMonths(now, 3)),
        to: endOfQuarter(subMonths(now, 3)),
      },
    },
    {
      label: translations.thisQuarter,
      range: {
        from: startOfQuarter(now),
        to: endOfQuarter(now),
      },
    },
    {
      label: translations.lastYear,
      range: {
        from: startOfYear(subYears(now, 1)),
        to: endOfYear(subYears(now, 1)),
      },
    },
    {
      label: translations.thisYear,
      range: {
        from: startOfYear(now),
        to: endOfYear(now),
      },
    },
  ]

  const visibleRanges =
    typeof maxRangeDays === 'number'
      ? ranges.filter(
          ({ range }) =>
            differenceInCalendarDays(range.to, range.from) + 1 <= maxRangeDays
        )
      : ranges

  return (
    <ul className="m-0 w-full list-none grid grid-cols-2 gap-1 border-b border-gray-100 p-2 md:w-auto md:block md:self-center md:border-b-0 md:border-r">
      {visibleRanges.map(({ label, range }) => {
        const isSelected =
          !!value?.from &&
          !!value.to &&
          isSameDay(value.from, range.from) &&
          isSameDay(value.to, range.to)

        return (
          <li key={label} className="md:not-first:mt-0.5">
            <button
              className={cn(
                'w-full py-1 px-4 rounded-lg cursor-pointer text-stone-800 text-left',
                !isSelected && 'hover:bg-primary-hover hover:text-stone-800',
                isSelected && 'bg-primary text-white'
              )}
              aria-label={`Select ${label}`}
              onClick={() => onClick(range)}
            >
              {label}
            </button>
          </li>
        )
      })}
    </ul>
  )
}
